import moment from 'moment'
import React, {FC, useCallback, useEffect, useMemo, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {socket} from 'entities/app/socket'
import {
  addMessageListDown,
  clearMessagesState,
  DialogItemType,
  GetMessages,
  GroupedMessagesType,
  openPrivatePhoto,
  useChatCanViewPrivatePhotos,
  useChatPairConnection,
  useIsMessageToEachOther,
  useMessagesChatRequest,
  useMessagesCursor,
  useMessagesList,
  useMessagesLoadingMedia,
  useMessagesLoadingMessage,
  useMockDialog,
  useOpenDialog,
} from 'entities/messages'
import {clearProfile, useProfile} from 'entities/profile'
import {usePrompt, useScroll} from 'shared/hooks'
import {Loader} from 'shared/ui/loader'
import {ChatBlock} from '../chatBlock'
import {ChatInput} from '../chatInput'
import {Message, MessageLoadingMedia, MessageText} from '../chatMessage'
import {ChatStartDialog} from '../chatStartDialog'
import {ChatUpgradeAccount} from '../chatUpgradeAccount'
import {ChatVirtualGifts} from '../chatVirtualGifts'
import styles from './styles.module.scss'

export const ChatMessagesList: FC<{userId: string}> = ({userId}) => {
  const dispatch = useDispatch()
  const ref = useRef<HTMLDivElement | null>(null)
  const scroll = useScroll(ref)
  const cursor = useMessagesCursor()
  const list = useMessagesList()
  const openDialog = useOpenDialog()
  const profile = useProfile()
  const {refetch, isError, isFetching} = GetMessages(userId, cursor, openDialog?.idLastReadMessage || '', dispatch)
  const {isBlock, isBlockMe} = useChatPairConnection()
  const mockDialog = useMockDialog()
  const isBlockUser = useMemo(
    () => isBlock || isBlockMe || openDialog?.interlocutor?.isBlocked || profile?.isBlocked,
    [isBlock, isBlockMe, openDialog?.interlocutor?.isBlocked, profile?.isBlocked]
  )
  const isViewPrivatePhotos = useChatCanViewPrivatePhotos()
  const isMessageToEachOther = useIsMessageToEachOther()

  const scrollBottom = useCallback(() => {
    ref.current && ref.current.scrollTo(0, ref.current.scrollHeight)
  }, [ref.current])

  // Отчистка данных при смене чата
  useEffect(() => {
    dispatch(clearProfile())
    dispatch(clearMessagesState())
  }, [userId])

  // Открытие приватных фото если есть сообщение друг другу
  useEffect(() => {
    if (isViewPrivatePhotos) return

    if (isMessageToEachOther) dispatch(openPrivatePhoto())
  }, [isMessageToEachOther, isViewPrivatePhotos])

  // socket
  useEffect(() => {
    const chatMessageHandler = (dialog: DialogItemType) => {
      const isBottomScroll = Boolean(ref.current && ref.current.scrollTop >= ref.current.scrollHeight - ref.current.offsetHeight)

      dispatch(addMessageListDown({messages: dialog.lastMessage, isBottom: isBottomScroll}))
      // Прокрутка вниз если отправителем является user
      if (dialog.lastMessage.to.id === userId) scrollBottom()
      // Прокрутка вниз если мы были внизу
      setTimeout(() => isBottomScroll && scrollBottom())
    }

    socket.on('chat.message', chatMessageHandler)
    return () => {
      socket.off('chat.message', chatMessageHandler)
    }
  }, [userId])

  // Начальная загрузка
  useEffect(() => {
    if (!openDialog || list.length) return

    refetch()
      .then(({data: response}) => {
        const isNewMessageElement = ref.current?.querySelector('[data-id="isNewMessage"]')

        isNewMessageElement
          ? ref.current?.scrollTo(0, isNewMessageElement.getBoundingClientRect().top)
          : response?.data?.data?.length && scrollBottom()
      })
  }, [userId, openDialog, list])

  // Прокрутка
  useEffect(() => {
    if (!ref.current) return
    if (isFetching || isError) return
    if (cursor == null) return
    if (!openDialog) return

    if (scroll > 0 && scroll < 300) {
      const lastScrollHeight = ref.current?.scrollHeight

      // TODO: Подумать как избавиться от костыля(запоминание scrollTop до выполениня запроса)
      let scrollTop: number = 0
      const interval = setInterval(() => {
        scrollTop = ref.current?.scrollTop || 0
      }, 10)

      refetch().then(() => {
        if (!ref.current) return

        ref.current.scrollTo(0, ref.current.scrollHeight - lastScrollHeight + scrollTop)
      }).finally(() => {
        clearInterval(interval)
      })
    }
  }, [scroll, ref.current, openDialog, refetch])

  return (
    <>
      <div className={`${styles.chatWrapper} ${styles.customScroll}`} ref={ref}>
        {
          (isFetching && cursor === '') || (openDialog === null && mockDialog === undefined)
            ? <div className={styles.loader}><Loader /></div>
            : list.length === 0 && !mockDialog?.chatRequest
              ? !isBlockUser ? <ChatStartDialog idUser={userId} /> : null
              : <div className={styles.messagesWrapper}>
                <ChatRequestMessage />
                {list.map((groupedMessage, index) => {
                  if (groupedMessage.date) return <DividerDate key={index} date={groupedMessage.date} />
                  if (groupedMessage.isNew) return <DividerIsNew key={index} />

                  if (groupedMessage.fromId) {
                    return <GroupedMessages
                      key={index}
                      group={groupedMessage}
                      isMyMessage={groupedMessage.fromId !== userId}
                    />
                  }
                })}
                <LoadingMedia />
                <LoadingMessage />
                <ChatUpgradeAccount />
              </div>
        }
      </div>

      {!isBlockUser
        ? <>
          <ChatVirtualGifts />
          <ChatInput scrollBottom={scrollBottom} userId={userId} />
        </>
        : <ChatBlock />}
    </>
  )
}

const ChatRequestMessage = () => {
  const chatRequest = useMessagesChatRequest()

  const time = useMemo(
    () => chatRequest?.dateCreated ? moment(chatRequest.dateCreated).format('HH:mm') : '',
    [chatRequest?.dateCreated]
  )

  if (!chatRequest) return <></>

  return (
    <>
      <DividerDate date={chatRequest.dateCreated} />
      <div className={styles.leftWrapper}>
        <MessageText time={time} text={chatRequest.content.message} isMyMessage={false} />
      </div>
    </>
  )
}

const LoadingMedia = () => {
  const loadingMedia = useMessagesLoadingMedia()

  usePrompt('Media is loading. When you exit the chat, the download will be interrupted.', Boolean(loadingMedia.length))

  if (!loadingMedia.length) return <></>

  return (
    <div className={styles.rightWrapper}>
      {loadingMedia.map((media) => <MessageLoadingMedia key={media.cfId} media={media} />)}
    </div>
  )
}

const LoadingMessage = () => {
  const loadingMessage = useMessagesLoadingMessage()

  if (!loadingMessage.length) return <></>

  return (
    <div className={styles.rightWrapper}>
      {loadingMessage.map((message) => <MessageText key={message.id} text={message.content.text} isMyMessage />)}
    </div>
  )
}

const DividerDate: FC<{date: string}> = ({date}) => {
  const dateFormat = useMemo(() => {
    const dateMoment = moment(date)

    if (dateMoment.isSame(moment(), 'day')) return 'Today'
    if (dateMoment.isSame(moment().subtract(1, 'days'), 'day')) return 'Yesterday'
    for (let i = 2; i <= 7; i++) {
      if (dateMoment.isSame(moment().subtract(i, 'days'), 'day')) return `${i} days ago`
    }
    if (dateMoment.isSame(moment(), 'year')) return dateMoment.format('DD MMMM')

    return dateMoment.format('DD MMMM YYYY')
  }, [date])

  return <div className={styles.dividerDateWrapper}>
    <div className={styles.dividerDate}>{dateFormat}</div>
  </div>
}

const DividerIsNew = () => {
  return (
    <div className={styles.dividerDateWrapper} data-id='isNewMessage'>
      <div className={styles.dividerDate}>New messages</div>
    </div>
  )
}

type GroupedMessagesProps = {
  group: GroupedMessagesType
  isMyMessage: boolean
}

const GroupedMessages: FC<GroupedMessagesProps> = ({group, isMyMessage}) => {
  return (
    <div className={isMyMessage ? styles.rightWrapper : styles.leftWrapper}>
      {group.messages.map((message) => (<Message key={message.id} message={message} isMyMessage={isMyMessage} />))}
    </div>
  )
}